"use client";
import { jsonLdScript } from "@/lib/json-ld";

import { useState, useEffect, useRef } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { motion, AnimatePresence } from "motion/react";
import {
  MapPin,
  Construction,
  ArrowRight,
  UserPlus,
  CheckCircle2,
  CreditCard,
  Zap,
  MessageSquare,
  Home,
  Wrench,
  Image as ImageIcon,
  Star,
  BadgeCheck,
  Navigation,
  Calendar,
  ShieldCheck,
  Menu,
  X,
} from "lucide-react";
import { TradePicker } from "./components/TradePicker";
import { ThemeToggle } from "./components/ThemeToggle";
import {
  FIND_A_TRADE_ITEMS,
  COSTS_AND_GUIDES_ITEMS,
} from "./components/Navbar";
import {
  LANDING_FOOTER_SECTIONS,
  LANDING_BOTTOM_LINKS,
} from "@/lib/footer-links";

/* ─────────────────────────── NAVBAR ─────────────────────────── */

function LandingNavbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [mobileAccordion, setMobileAccordion] = useState<string | null>(null);
  const hoverCloseRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const findRef = useRef<HTMLDivElement>(null);
  const costsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = isMobileMenuOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [isMobileMenuOpen]);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (
        openDropdown &&
        (!findRef.current || !findRef.current.contains(e.target as Node)) &&
        (!costsRef.current || !costsRef.current.contains(e.target as Node))
      ) {
        setOpenDropdown(null);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [openDropdown]);

  function handleDropdownEnter(name: string) {
    clearTimeout(hoverCloseRef.current);
    setOpenDropdown(name);
  }

  function handleDropdownLeave() {
    hoverCloseRef.current = setTimeout(() => setOpenDropdown(null), 150);
  }

  return (
    <header
      className={`fixed top-0 w-full z-50 transition-all duration-300 ${
        isScrolled
          ? "bg-warm-bg/80 dark:bg-[#0f172a]/80 backdrop-blur-xl border-b border-border-light dark:border-white/[0.06] py-3"
          : "bg-transparent py-5"
      }`}
    >
      <nav className="flex justify-between items-center w-full px-6 md:px-8 max-w-7xl mx-auto">
        <Link
          href="/"
          className="text-2xl font-extrabold tracking-tight text-forest no-underline"
        >
          Tradesfolk
        </Link>
        <div className="hidden md:flex gap-1 items-center text-sm font-semibold">
          <div
            ref={findRef}
            className="relative"
            onMouseEnter={() => handleDropdownEnter("find")}
            onMouseLeave={handleDropdownLeave}
          >
            <button
              onClick={() => setOpenDropdown(openDropdown === "find" ? null : "find")}
              aria-expanded={openDropdown === "find"}
              aria-haspopup="true"
              className={`flex items-center gap-1 px-4 py-2 rounded-lg font-semibold transition-colors bg-transparent border-none cursor-pointer font-sans ${
                openDropdown === "find" ? "text-forest" : "text-charcoal hover:text-forest"
              }`}
            >
              Find a Trade
              <svg
                width="10"
                height="10"
                viewBox="0 0 10 10"
                fill="none"
                className={`transition-transform duration-200 ${openDropdown === "find" ? "rotate-180" : ""}`}
              >
                <path
                  d="M2.5 3.75L5 6.25L7.5 3.75"
                  stroke="currentColor"
                  strokeWidth="1.4"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
            <div
              className={`absolute top-[calc(100%+8px)] left-0 bg-white dark:bg-slate-800 rounded-xl shadow-[0_12px_40px_rgba(0,0,0,0.1),0_0_0_1px_rgba(0,0,0,0.04)] dark:shadow-[0_12px_40px_rgba(0,0,0,0.4),0_0_0_1px_rgba(255,255,255,0.06)] p-2 min-w-[280px] transition-all duration-200 ${
                openDropdown === "find"
                  ? "opacity-100 pointer-events-auto translate-y-0"
                  : "opacity-0 pointer-events-none -translate-y-1"
              }`}
            >
              {FIND_A_TRADE_ITEMS.map((item) => (
                <Link
                  key={item.href}
                  href={item.href}
                  className="block px-3.5 py-2.5 no-underline rounded-lg transition-all hover:bg-warm-bg dark:hover:bg-slate-700 group"
                  onClick={() => setOpenDropdown(null)}
                >
                  <span className="flex items-center gap-2">
                    <span className="text-[13px] font-medium text-text-body group-hover:text-forest">
                      {item.label}
                    </span>
                    {item.badge && (
                      <span className="text-[10px] font-bold uppercase tracking-wide text-forest bg-forest/10 px-1.5 py-0.5 rounded-sm">
                        {item.badge}
                      </span>
                    )}
                  </span>
                  <span className="text-[11.5px] text-text-muted mt-0.5 block">
                    {item.description}
                  </span>
                </Link>
              ))}
            </div>
          </div>
          <div
            ref={costsRef}
            className="relative"
            onMouseEnter={() => handleDropdownEnter("costs")}
            onMouseLeave={handleDropdownLeave}
          >
            <button
              onClick={() => setOpenDropdown(openDropdown === "costs" ? null : "costs")}
              aria-expanded={openDropdown === "costs"}
              aria-haspopup="true"
              className={`flex items-center gap-1 px-4 py-2 rounded-lg font-semibold transition-colors bg-transparent border-none cursor-pointer font-sans ${
                openDropdown === "costs" ? "text-forest" : "text-charcoal hover:text-forest"
              }`}
            >
              Costs and Guides
              <svg
                width="10"
                height="10"
                viewBox="0 0 10 10"
                fill="none"
                className={`transition-transform duration-200 ${openDropdown === "costs" ? "rotate-180" : ""}`}
              >
                <path
                  d="M2.5 3.75L5 6.25L7.5 3.75"
                  stroke="currentColor"
                  strokeWidth="1.4"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
            <div
              className={`absolute top-[calc(100%+8px)] left-0 bg-white dark:bg-slate-800 rounded-xl shadow-[0_12px_40px_rgba(0,0,0,0.1),0_0_0_1px_rgba(0,0,0,0.04)] dark:shadow-[0_12px_40px_rgba(0,0,0,0.4),0_0_0_1px_rgba(255,255,255,0.06)] p-2 min-w-[280px] transition-all duration-200 ${
                openDropdown === "costs"
                  ? "opacity-100 pointer-events-auto translate-y-0"
                  : "opacity-0 pointer-events-none -translate-y-1"
              }`}
            >
              {COSTS_AND_GUIDES_ITEMS.map((item) => (
                <Link
                  key={item.href}
                  href={item.href}
                  className="block px-3.5 py-2.5 no-underline rounded-lg transition-all hover:bg-warm-bg dark:hover:bg-slate-700 group"
                  onClick={() => setOpenDropdown(null)}
                >
                  <span className="flex items-center gap-2">
                    <span className="text-[13px] font-medium text-text-body group-hover:text-forest">
                      {item.label}
                    </span>
                    {item.badge && (
                      <span className="text-[10px] font-bold uppercase tracking-wide text-forest bg-forest/10 px-1.5 py-0.5 rounded-sm">
                        {item.badge}
                      </span>
                    )}
                  </span>
                  <span className="text-[11.5px] text-text-muted mt-0.5 block">
                    {item.description}
                  </span>
                </Link>
              ))}
            </div>
          </div>
          <Link
            href="/how-it-works"
            className="text-charcoal hover:text-forest transition-colors no-underline px-4 py-2"
          >
            How It Works
          </Link>
          <Link
            href="/for-tradespeople"
            className="text-charcoal hover:text-forest transition-colors no-underline px-4 py-2"
          >
            For Tradespeople
          </Link>
        </div>
        <div className="flex items-center gap-3">
          <ThemeToggle />
          <Link
            href="/login"
            className="hidden sm:block text-sm font-bold text-forest hover:opacity-80 no-underline"
          >
            Log In
          </Link>
          <Link
            href="/signup/tradesperson"
            className="bg-forest text-white px-6 py-3 rounded-xl font-bold active:scale-95 transition-transform shadow-lg shadow-forest/20 no-underline"
          >
            List for Free
          </Link>
          <button
            className="md:hidden min-h-11 min-w-11 flex items-center justify-center text-charcoal bg-transparent border-none cursor-pointer"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label="Menu"
            aria-expanded={isMobileMenuOpen}
            aria-controls="landing-mobile-navigation"
          >
            {isMobileMenuOpen ? <X /> : <Menu />}
          </button>
        </div>
      </nav>
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            id="landing-mobile-navigation"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="absolute top-full left-0 w-full bg-white dark:bg-slate-800 border-b border-border dark:border-slate-700 px-2 py-4 flex flex-col md:hidden shadow-xl"
          >
            <button
              onClick={() => setMobileAccordion(mobileAccordion === "find" ? null : "find")}
              aria-expanded={mobileAccordion === "find"}
              className="flex items-center justify-between w-full px-4 py-3.5 text-lg font-semibold text-charcoal bg-transparent border-none cursor-pointer font-sans rounded-lg active:bg-warm-bg"
            >
              Find a Trade
              <svg
                width="14"
                height="14"
                viewBox="0 0 14 14"
                fill="none"
                className={`transition-transform duration-200 ${mobileAccordion === "find" ? "rotate-180" : ""}`}
              >
                <path
                  d="M3.5 5.25L7 8.75L10.5 5.25"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
            <div
              className={`overflow-hidden transition-all duration-200 ${
                mobileAccordion === "find" ? "max-h-[400px] opacity-100" : "max-h-0 opacity-0"
              }`}
            >
              <div className="pl-4 pb-2">
                {FIND_A_TRADE_ITEMS.map((item) => (
                  <Link
                    key={item.href}
                    href={item.href}
                    className="block px-4 py-3 no-underline rounded-lg text-charcoal active:bg-warm-bg"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <span className="flex items-center gap-2">
                      <span className="text-[14px] font-medium">{item.label}</span>
                      {item.badge && (
                        <span className="text-[9px] font-bold uppercase tracking-wide text-forest bg-forest/10 px-1.5 py-0.5 rounded-sm">
                          {item.badge}
                        </span>
                      )}
                    </span>
                    <span className="text-[12px] text-text-muted mt-0.5 block">
                      {item.description}
                    </span>
                  </Link>
                ))}
              </div>
            </div>
            <button
              onClick={() => setMobileAccordion(mobileAccordion === "costs" ? null : "costs")}
              aria-expanded={mobileAccordion === "costs"}
              className="flex items-center justify-between w-full px-4 py-3.5 text-lg font-semibold text-charcoal bg-transparent border-none cursor-pointer font-sans rounded-lg active:bg-warm-bg"
            >
              Costs and Guides
              <svg
                width="14"
                height="14"
                viewBox="0 0 14 14"
                fill="none"
                className={`transition-transform duration-200 ${mobileAccordion === "costs" ? "rotate-180" : ""}`}
              >
                <path
                  d="M3.5 5.25L7 8.75L10.5 5.25"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
            <div
              className={`overflow-hidden transition-all duration-200 ${
                mobileAccordion === "costs" ? "max-h-[600px] opacity-100" : "max-h-0 opacity-0"
              }`}
            >
              <div className="pl-4 pb-2">
                {COSTS_AND_GUIDES_ITEMS.map((item) => (
                  <Link
                    key={item.href}
                    href={item.href}
                    className="block px-4 py-3 no-underline rounded-lg text-charcoal active:bg-warm-bg"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <span className="flex items-center gap-2">
                      <span className="text-[14px] font-medium">{item.label}</span>
                    </span>
                    <span className="text-[12px] text-text-muted mt-0.5 block">
                      {item.description}
                    </span>
                  </Link>
                ))}
              </div>
            </div>
            <Link
              href="/how-it-works"
              className="block px-4 py-3.5 text-lg font-semibold text-charcoal no-underline rounded-lg active:bg-warm-bg"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              How It Works
            </Link>
            <Link
              href="/for-tradespeople"
              className="block px-4 py-3.5 text-lg font-semibold text-charcoal no-underline rounded-lg active:bg-warm-bg"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              For Tradespeople
            </Link>
            <div className="h-px bg-border-light mx-4 my-2" />
            <Link
              href="/login"
              className="block px-4 py-3.5 text-lg font-bold text-forest no-underline"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              Log In
            </Link>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}

/* ─────────────────────────── HERO ─────────────────────────── */

// The video sits below the first screen on phones, so load it on demand.
function HeroVideo() {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [ready, setReady] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(entries => {
      if (entries.some(entry => entry.isIntersecting)) {
        setReducedMotion(window.matchMedia("(prefers-reduced-motion: reduce)").matches);
        setReady(true);
        observer.disconnect();
      }
    });
    if (videoRef.current) observer.observe(videoRef.current);
    return () => observer.disconnect();
  }, []);
  return <video ref={videoRef} poster="/hero-trades-poster.jpg" preload="none" autoPlay={ready && !reducedMotion} muted loop playsInline controls={reducedMotion} aria-label="Tradespeople at work" className="w-full h-full object-cover">
    {ready && <source src="/hero-trades.mp4" type="video/mp4" />}
  </video>;
}

function Hero() {
  const router = useRouter();
  const [postcode, setPostcode] = useState("");
  const [trade, setTrade] = useState("");

  function handleSearch(e: React.FormEvent) {
    e.preventDefault();
    const params = new URLSearchParams();
    if (postcode) params.set("postcode", postcode);
    if (trade) params.set("trade", trade);
    router.push(`/browse?${params.toString()}`);
  }

  return (
    <section className="grain relative max-w-7xl mx-auto px-6 md:px-8 pt-22 pb-16 md:pt-32 md:pb-24 flex flex-col lg:flex-row items-center gap-12 lg:gap-16">
      {/* Soft radial gradient wash — sage tint from top-left */}
      <div
        className="absolute inset-0 pointer-events-none"
        aria-hidden="true"
        style={{
          background:
            "radial-gradient(ellipse 80% 70% at 10% 20%, rgba(122,155,111,0.05) 0%, transparent 70%)",
        }}
      />
      <div
        className="relative z-2 w-full lg:w-1/2 space-y-10"
      >
        <div className="space-y-6">
          <h1 className="text-5xl md:text-7xl font-extrabold tracking-tighter leading-[1.1] text-charcoal">
            Find local tradespeople.
            <br />
            <span className="text-forest italic">Free forever.</span>
          </h1>
          <p className="text-lg md:text-xl text-text-body max-w-lg leading-relaxed">
            No subscription fees, no lead costs, and no barriers. We connect
            homeowners with local tradespeople. Local choice may be limited while
            Tradesfolk grows, and replies or quotes are not guaranteed.
          </p>
        </div>
        <div className="space-y-6">
          <form
            onSubmit={handleSearch}
            className="bg-white dark:bg-slate-800 p-2 rounded-2xl shadow-2xl shadow-stone/30 dark:shadow-black/30 border border-border-light dark:border-slate-700 flex flex-col md:flex-row gap-2 max-w-2xl"
          >
            <div className="flex-1 relative">
              <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 text-text-muted w-5 h-5" />
              <input
                type="text"
                placeholder="Postcode"
                value={postcode}
                onChange={(e) => setPostcode(e.target.value)}
                aria-label="Postcode"
                className="w-full pl-12 pr-4 py-4 bg-cream border-none rounded-xl focus:ring-2 focus:ring-forest/20 text-charcoal placeholder:text-text-muted outline-none"
              />
            </div>
            <div className="flex-1 relative">
              <Construction className="absolute left-4 top-1/2 -translate-y-1/2 text-text-muted w-5 h-5 pointer-events-none z-10" />
              <TradePicker
                value={trade}
                onChange={setTrade}
                placeholder="Select Trade"
                ariaLabel="Trade category"
                className="w-full pl-12 pr-4 py-4 bg-cream rounded-xl text-sm"
              />
            </div>
            <button
              type="submit"
              className="bg-forest text-white px-10 py-4 rounded-xl font-bold text-lg active:scale-95 transition-transform shadow-lg shadow-forest/20 border-none cursor-pointer"
            >
              Search
            </button>
          </form>
          <div className="flex flex-wrap gap-4 text-sm">
            <Link
              href="/browse"
              className="flex items-center gap-2 font-bold text-white bg-forest hover:bg-forest-light px-5 py-3 rounded-xl shadow-lg shadow-forest/20 transition-all no-underline"
            >
              I need a tradesperson <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              href="/signup/tradesperson"
              className="flex items-center gap-2 font-bold text-white bg-copper hover:bg-copper-light px-5 py-3 rounded-xl shadow-lg shadow-copper/20 transition-all no-underline"
            >
              I am a tradesperson, list for free{" "}
              <UserPlus className="w-4 h-4" />
            </Link>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-6">
            <Link
              href="/snap"
              className="inline-flex items-center gap-2 text-sm font-semibold text-forest hover:text-forest-light transition-colors no-underline"
            >
              <ImageIcon className="w-4 h-4" />
              Not sure what you need? Snap a photo for a diagnosis.
            </Link>
            <Link
              href="/map"
              className="inline-flex items-center gap-2 text-sm font-semibold text-copper hover:text-copper-light transition-colors no-underline"
            >
              <MapPin className="w-4 h-4" />
              See what&apos;s happening near you
            </Link>
          </div>
        </div>
      </div>
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.6, delay: 0.2 }}
        className="relative z-2 w-full lg:w-1/2"
      >
        <div className="aspect-square rounded-3xl overflow-hidden shadow-2xl relative">
          <HeroVideo />
          <div className="absolute inset-0 bg-black/5 pointer-events-none" />
        </div>
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.8, duration: 0.5 }}
          className="absolute -bottom-8 -left-4 md:-left-8 bg-white p-6 rounded-2xl shadow-2xl border border-border-light flex items-center gap-4 max-w-xs"
        >
          <div className="w-12 h-12 rounded-full bg-sage-light-bg flex items-center justify-center">
            <CheckCircle2 className="text-forest w-6 h-6 fill-primary/20" />
          </div>
          <div>
            <p className="font-bold text-charcoal">Check Before Hiring</p>
            <p className="text-xs text-text-muted">
              Ask for qualifications and insurance
            </p>
          </div>
        </motion.div>
      </motion.div>
    </section>
  );
}

/* ─────────────────────── VALUE PROPS ─────────────────────── */

function ValueProps() {
  const props = [
    {
      icon: <CreditCard className="text-forest w-7 h-7" />,
      title: "No subscription fees",
      desc: "Keep more of what you earn.",
    },
    {
      icon: <Zap className="text-forest w-7 h-7" />,
      title: "No lead fees",
      desc: "Connecting without the tax.",
    },
    {
      icon: <MessageSquare className="text-forest w-7 h-7" />,
      title: "No cost to contact",
      desc: "Direct, free communication.",
    },
  ];
  return (
    <section className="bg-cream py-16 border-y border-border-light">
      <div className="max-w-7xl mx-auto px-6 md:px-8 grid md:grid-cols-3 gap-12">
        {props.map((prop, i) => (
          <div key={i} className="flex items-center gap-5">
            <div className="w-14 h-14 rounded-2xl bg-white flex items-center justify-center shrink-0 shadow-sm">
              {prop.icon}
            </div>
            <div>
              <h2 className="font-bold text-lg">{prop.title}</h2>
              <p className="text-text-muted text-sm">{prop.desc}</p>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

/* ─────────────────────── HOW IT WORKS ─────────────────────── */

function HowItWorks() {
  return (
    <section id="how-it-works" className="max-w-7xl mx-auto px-6 md:px-8 py-16 md:py-24">
      <div className="text-center mb-12 md:mb-16 space-y-4">
        <h2 className="text-4xl md:text-5xl font-bold tracking-tight">
          How Tradesfolk Works
        </h2>
        <p className="text-text-muted text-lg">
          A simple, transparent journey for everyone.
        </p>
      </div>
      <div className="grid lg:grid-cols-2 gap-12">
        <motion.div
          whileHover={{ y: -5 }}
          className="bg-white p-10 md:p-12 rounded-5xl shadow-sm border border-border-light space-y-10"
        >
          <div className="flex items-center gap-4">
            <Home className="text-copper w-9 h-9" />
            <h3 className="text-2xl font-bold">For Homeowners</h3>
          </div>
          <div className="space-y-8">
            {[
              {
                step: 1,
                title: "Search.",
                desc: "Find local tradespeople by postcode and trade type.",
              },
              {
                step: 2,
                title: "Browse Profiles.",
                desc: "View work galleries, reviews, and credentials.",
              },
              {
                step: 3,
                title: "Contact Directly or Post a Job.",
                desc: "No paywalls. Message or call for free, or post your job and let tradespeople come to you.",
              },
            ].map((item) => (
              <div key={item.step} className="flex gap-6">
                <div className="w-10 h-10 rounded-full bg-cream flex items-center justify-center shrink-0 font-bold text-text-body">
                  {item.step}
                </div>
                <p className="text-lg">
                  <span className="font-bold block">{item.title}</span>{" "}
                  {item.desc}
                </p>
              </div>
            ))}
          </div>
        </motion.div>
        <motion.div
          whileHover={{ y: -5 }}
          className="bg-forest text-white p-10 md:p-12 rounded-5xl shadow-2xl space-y-10"
        >
          <div className="flex items-center gap-4">
            <Wrench className="text-sage-light-bg w-9 h-9" />
            <h3 className="text-2xl font-bold">For Tradespeople</h3>
          </div>
          <div className="space-y-8">
            {[
              {
                step: 1,
                title: "Create Profile.",
                desc: "Showcase your best work and list qualifications.",
              },
              {
                step: 2,
                title: "Get Found.",
                desc: "Appear in local searches without paying for position.",
              },
              {
                step: 3,
                title: "Build Reputation.",
                desc: "Collect verified reviews from happy clients.",
              },
            ].map((item) => (
              <div key={item.step} className="flex gap-6">
                <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center shrink-0 font-bold text-sage-light-bg">
                  {item.step}
                </div>
                <p className="text-lg text-sage-light-bg/90">
                  <span className="font-bold block text-white">
                    {item.title}
                  </span>{" "}
                  {item.desc}
                </p>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
}

/* ─────────────────────── FEATURES ─────────────────────── */

function Features() {
  const features = [
    {
      icon: <ImageIcon className="w-8 h-8" />,
      title: "Work Photo Galleries",
      desc: "Visual evidence of previous craftsmanship and project finishes.",
    },
    {
      icon: <Star className="w-8 h-8" />,
      title: "Customer Reviews",
      desc: "Read customer feedback where available and ask for references.",
    },
    {
      icon: <BadgeCheck className="w-8 h-8" />,
      title: "Qualification Badges",
      desc: "Tradespeople can list qualifications. Check current registration with the relevant trade body.",
    },
    {
      icon: <Navigation className="w-8 h-8" />,
      title: "Postcode Search",
      desc: "Find professionals who actually work in your specific local area.",
    },
    {
      icon: <MessageSquare className="w-8 h-8" />,
      title: "Direct Messaging",
      desc: "Secure in-app chat to discuss project details and share quotes.",
    },
    {
      icon: <Construction className="w-8 h-8" />,
      title: "Post a Job",
      desc: "Describe what you need. Tradespeople in your area respond. You pick who to talk to.",
    },
  ];
  return (
    <section className="max-w-7xl mx-auto px-6 md:px-8 py-16 md:py-24">
      <div className="text-center mb-10 md:mb-14">
        <h2 className="text-4xl font-bold tracking-tight mb-4">
          Built for Transparency
        </h2>
        <p className="text-text-muted">
          Every feature is designed to foster genuine connections.
        </p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        {features.map((f, i) => (
          <motion.div
            key={i}
            whileHover={{ scale: 1.02 }}
            className="bg-white p-8 rounded-3xl border border-border-light shadow-sm hover:shadow-md transition-all"
          >
            <div className="text-forest mb-4">{f.icon}</div>
            <h3 className="font-bold text-xl mb-2">{f.title}</h3>
            <p className="text-text-muted text-sm leading-relaxed">{f.desc}</p>
          </motion.div>
        ))}
      </div>
    </section>
  );
}

/* ─────────────────── COMPARISON TABLE ─────────────────── */

function ComparisonTable() {
  const rows = [
    { feature: "Monthly fee", typical: "£20 - £60+", tradesfolk: "£0" },
    { feature: "Cost per lead", typical: "£5 - £50", tradesfolk: "£0" },
    {
      feature: "Featured listing cost",
      typical: "Paid Extra",
      tradesfolk: "Free",
    },
    {
      feature: "Annual cost",
      typical: "£500 - £3,000+",
      tradesfolk: "£0",
    },
    {
      feature: "Direct contact",
      typical: "Often Restricted",
      tradesfolk: "Always Free",
      highlight: true,
    },
    {
      feature: "Verified reviews",
      typical: "Included",
      tradesfolk: "Included",
    },
  ];
  return (
    <section
      id="comparison"
      className="bg-charcoal py-16 md:py-24 text-white overflow-hidden relative"
    >
      <div className="max-w-4xl mx-auto px-6 md:px-8 relative z-10">
        <div className="text-center mb-10 md:mb-14">
          <h2 className="text-4xl font-bold mb-4">Tired of paying to work?</h2>
          <p className="text-white/75">
            Comparing the true cost of lead-generation platforms.
          </p>
        </div>
        {/* Desktop table */}
        <div className="hidden sm:block overflow-hidden rounded-3xl border border-stone-700 bg-stone-900/50 backdrop-blur-sm">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-stone-800">
                <th className="p-5 text-xs font-bold uppercase tracking-widest text-white/75">
                  Feature
                </th>
                <th className="p-5 text-xs font-bold uppercase tracking-widest text-white/75">
                  Typical Platforms
                </th>
                <th className="p-5 text-xs font-bold uppercase tracking-widest text-[#86EFAC] bg-[#86EFAC]/10">
                  Tradesfolk
                </th>
              </tr>
            </thead>
            <tbody className="text-stone text-sm">
              {rows.map((row, i) => (
                <tr
                  key={i}
                  className="border-b border-stone-800 last:border-0"
                >
                  <td className="p-5 font-medium text-white">{row.feature}</td>
                  <td
                    className={`p-5 ${row.highlight ? "text-red-300" : ""}`}
                  >
                    {row.typical}
                  </td>
                  <td className="p-5 text-[#86EFAC] font-extrabold text-base bg-[#86EFAC]/5">
                    {row.tradesfolk}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Mobile stacked cards */}
        <div className="sm:hidden space-y-3">
          {rows.map((row, i) => (
            <div
              key={i}
              className="rounded-xl bg-white/5 p-4"
            >
              <p className="text-xs font-semibold uppercase tracking-wider text-white/75 mb-3">
                {row.feature}
              </p>
              <div className="flex justify-between items-center gap-4">
                <div>
                  <p className="text-[11px] text-white/75 mb-0.5">Typical</p>
                  <p className={`text-sm font-medium ${row.highlight ? "text-red-300" : "text-stone"}`}>
                    {row.typical}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-[11px] text-[#86EFAC]/70 mb-0.5">Tradesfolk</p>
                  <p className="text-base font-extrabold text-[#86EFAC]">
                    {row.tradesfolk}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-forest/5 rounded-full blur-[120px]" />
    </section>
  );
}

/* ─────────────────── PROMISE BANNER ─────────────────── */

function PromiseBanner() {
  return (
    <section className="bg-sage-light-bg py-16 md:py-24">
      <div className="max-w-4xl mx-auto px-6 md:px-8 text-center space-y-6">
        <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-forest/10 text-forest text-xs font-bold uppercase tracking-wide">
          <Calendar className="w-3.5 h-3.5" /> Just Launched
        </span>
        <h2 className="text-4xl font-bold text-sage-dark tracking-tight">
          Tradesfolk is completely free for everyone.
        </h2>
        <p className="text-xl text-sage-dark/80 leading-relaxed max-w-2xl mx-auto">
          We don&apos;t believe tradespeople should have to pay for the
          privilege of working. No subscription fees, no lead fees, no
          commission. Not now, not ever. We make money from partner services
          (like trade insurance and tool suppliers), never from our users.
        </p>
        <div className="pt-4">
          <span className="inline-flex items-center gap-2 px-6 py-2.5 bg-white/50 rounded-full text-sage-dark font-bold text-sm">
            <ShieldCheck className="w-5 h-5" /> 100% Funded via Partners
          </span>
        </div>
      </div>
    </section>
  );
}

/* ─────────────────── FOOTER ─────────────────── */

function LandingFooter() {
  return (
    <footer className="bg-cream w-full py-14 md:py-20 px-6 md:px-8 text-sm">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-12 max-w-7xl mx-auto">
        <div className="col-span-2 md:col-span-1 space-y-6">
          <Link
            href="/"
            className="text-2xl font-bold text-forest no-underline"
          >
            Tradesfolk
          </Link>
          <p className="text-text-muted max-w-xs leading-relaxed">
            The UK&apos;s only truly free directory for local trades and
            homeowners. Built on trust, not fees.
          </p>
        </div>
        {LANDING_FOOTER_SECTIONS.map((section) => (
          <div key={section.heading}>
            <h2 className="font-bold text-charcoal mb-6">{section.heading}</h2>
            <ul className="space-y-4 list-none p-0 m-0">
              {section.links.map((link) => (
                <li key={link.href}>
                  <Link
                    href={link.href}
                    className="text-text-muted hover:text-forest transition-colors no-underline"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
      <div className="max-w-7xl mx-auto mt-20 pt-8 border-t border-border flex flex-col md:flex-row justify-between items-center gap-6">
        <p className="text-text-muted font-medium">
          &copy; {new Date().getFullYear()} Tradesfolk. Free for tradespeople, free for homeowners, free forever.
        </p>
        <div className="flex gap-6">
          {LANDING_BOTTOM_LINKS.map((link) => (
            <Link
              key={link.href}
              href={link.href}
              className="text-text-muted hover:text-forest transition-colors no-underline"
            >
              {link.label}
            </Link>
          ))}
        </div>
      </div>
    </footer>
  );
}

/* ─────────────────── PAGE ─────────────────── */

const HOME_JSONLD = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "WebSite",
      name: "Tradesfolk",
      url: "https://tradesfolk.co",
      potentialAction: {
        "@type": "SearchAction",
        target: {
          "@type": "EntryPoint",
          urlTemplate: "https://tradesfolk.co/browse?postcode={postcode}&trade={trade}",
        },
        "query-input": [
          "required name=postcode",
          "required name=trade",
        ],
      },
    },
    {
      "@type": "Organization",
      name: "Tradesfolk",
      url: "https://tradesfolk.co",
      logo: "https://tradesfolk.co/icon-512.png",
      description:
        "Free marketplace connecting homeowners with local tradespeople. No fees for tradespeople, no fees for homeowners. Free forever.",
      contactPoint: {
        "@type": "ContactPoint",
        email: "hello@tradesfolk.co",
        contactType: "customer service",
      },
    },
  ],
};

export default function HomePage() {
  return (
    <div className="bg-warm-bg">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: jsonLdScript(HOME_JSONLD) }}
      />
      <LandingNavbar />
      <Hero />
      <ValueProps />
      <HowItWorks />
      <Features />
      <ComparisonTable />
      <PromiseBanner />
      <LandingFooter />
    </div>
  );
}
